import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Sprint CMA-B — New Climara Campaign dialog.
 *
 * Reuses ONLY public services:
 *   - GET  /api/admin/clients              → company list
 *   - GET  /api/admin/users                → invited respondents (filtered to 'evaluated')
 *   - GET  /api/admin/questionnaires       → published 'climara' evaluations from the Library
 *   - GET  /api/admin/departments          → optional scope
 *   - POST /api/admin/climara/campaigns    → create + spawn assignments
 *
 * Does NOT touch any frozen suite, does NOT hardcode dimensions.
 */
export default function ClimaraNewCampaignDialog({
  open,
  onOpenChange,
  companyId,
  onCreated,
}) {
  const { t } = useTranslation();

  const [questionnaires, setQuestionnaires] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [qid, setQid] = useState('');
  const [name, setName] = useState('');
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
  });
  const [deptFilter, setDeptFilter] = useState('__all__');
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load form data whenever the dialog opens for a company
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [qs, depts, allUsers] = await Promise.all([
          adminApi.listQuestionnaires(),
          adminApi.listDepartments(companyId, false),
          adminApi.listAllUsers(),
        ]);
        if (cancelled) return;
        const climaraQs = (Array.isArray(qs) ? qs : qs?.items || [])
          .filter(q => q.status === 'published'
            && q.current_version_id
            && (q.suite_codes || []).includes('climara'));
        setQuestionnaires(climaraQs);
        setDepartments(depts || []);
        const evaluated = (allUsers || [])
          .filter(u => u.role === 'evaluated' && u.company_id === companyId);
        setUsers(evaluated);
        if (climaraQs.length && !qid) setQid(climaraQs[0].id);
        if (!name) setName(`Climara — ${period}`);
        setSelected({});
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const filteredUsers = useMemo(() => {
    if (deptFilter === '__all__') return users;
    if (deptFilter === '__none__') return users.filter(u => !u.department_id);
    return users.filter(u => u.department_id === deptFilter);
  }, [users, deptFilter]);

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected],
  );

  const toggleAll = (checked) => {
    const next = { ...selected };
    filteredUsers.forEach(u => { next[u.id] = !!checked; });
    setSelected(next);
  };

  const submit = async () => {
    if (!qid) return toast.error(t('climara.campaign.errMissingQ', { defaultValue: 'Pick a questionnaire' }));
    if (!selectedIds.length) return toast.error(t('climara.campaign.errMissingUsers', { defaultValue: 'Pick at least one respondent' }));
    setSubmitting(true);
    try {
      const camp = await adminApi.climaraCreateCampaign({
        company_id: companyId,
        questionnaire_id: qid,
        name: name.trim() || `Climara — ${period}`,
        period: period.trim(),
        department_ids: deptFilter === '__all__' || deptFilter === '__none__' ? [] : [deptFilter],
        invited_user_ids: selectedIds,
      });
      toast.success(t('climara.campaign.created', {
        defaultValue: 'Campaign created with {{n}} invitations',
        n: camp.assignment_ids?.length || selectedIds.length,
      }));
      onCreated?.(camp);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="climara-new-campaign-dialog">
        <DialogHeader>
          <DialogTitle>{t('climara.campaign.newTitle', { defaultValue: 'New Climara campaign' })}</DialogTitle>
          <DialogDescription>
            {t('climara.campaign.newSub', {
              defaultValue: 'Reuses the Evaluation Library questionnaire and the shared Assignment engine. Anonymous responses only.',
            })}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cma-name">{t('climara.campaign.name', { defaultValue: 'Campaign name' })}</Label>
                <Input
                  id="cma-name" value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="climara-campaign-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cma-period">{t('climara.campaign.period', { defaultValue: 'Period' })}</Label>
                <Input
                  id="cma-period" value={period}
                  onChange={e => setPeriod(e.target.value)}
                  placeholder="2026-Q1"
                  data-testid="climara-campaign-period"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t('climara.campaign.questionnaire', { defaultValue: 'Library questionnaire' })}</Label>
              <Select value={qid} onValueChange={setQid}>
                <SelectTrigger data-testid="climara-campaign-questionnaire"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {questionnaires.length === 0 && (
                    <SelectItem value="__empty__" disabled>
                      {t('climara.campaign.noPublished', { defaultValue: 'No published Climara questionnaires' })}
                    </SelectItem>
                  )}
                  {questionnaires.map(q => (
                    <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{t('climara.campaign.respondents', { defaultValue: 'Invite respondents' })}</Label>
                <span className="text-xs text-muted-foreground" data-testid="climara-selected-count">
                  {selectedIds.length} / {filteredUsers.length} {t('climara.campaign.selected', { defaultValue: 'selected' })}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Select value={deptFilter} onValueChange={setDeptFilter}>
                  <SelectTrigger className="w-60" data-testid="climara-campaign-dept-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{t('climara.campaign.allDepts', { defaultValue: 'All departments' })}</SelectItem>
                    <SelectItem value="__none__">{t('climara.campaign.noDept', { defaultValue: 'Unassigned' })}</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name_es || d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline" size="sm"
                  onClick={() => toggleAll(selectedIds.length < filteredUsers.length)}
                  data-testid="climara-campaign-toggle-all"
                >
                  {selectedIds.length < filteredUsers.length
                    ? t('climara.campaign.selectAll', { defaultValue: 'Select all' })
                    : t('climara.campaign.unselectAll', { defaultValue: 'Unselect all' })}
                </Button>
              </div>

              <div className="border rounded-md max-h-64 overflow-y-auto divide-y" data-testid="climara-campaign-user-list">
                {filteredUsers.length === 0 && (
                  <p className="p-4 text-sm text-muted-foreground text-center">
                    {t('climara.campaign.noUsers', { defaultValue: 'No evaluated users in this company yet.' })}
                  </p>
                )}
                {filteredUsers.map(u => (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 p-2 hover:bg-muted/40 cursor-pointer text-sm"
                    data-testid={`climara-campaign-user-${u.id}`}
                  >
                    <Checkbox
                      checked={!!selected[u.id]}
                      onCheckedChange={(v) => setSelected(s => ({ ...s, [u.id]: !!v }))}
                    />
                    <span className="font-medium flex-1 truncate">{u.name || u.email}</span>
                    {u.department_id && (
                      <Badge variant="outline" className="text-xs">
                        {(departments.find(d => d.id === u.department_id)?.name_es)
                          || departments.find(d => d.id === u.department_id)?.name
                          || '—'}
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="climara-campaign-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={submit}
            disabled={loading || submitting || !qid || !selectedIds.length}
            className="bg-gradient-primary hover:opacity-90"
            data-testid="climara-campaign-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {t('climara.campaign.create', { defaultValue: 'Create campaign' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
